import toastManager from "../toastManager";

import {
  sanitizePairConfiguration,
  sanitizeImageDetails,
  sanitizeTags,
  appendImagesToFormDataCustomField,
  getValidPairConfiguration,
  sanitizeEntryDataResponseBase,
  processSettingImagePairs,
} from "./commonUtils";

/**
 * Sanitizes the data response for a comparison entry by ensuring that all
 * expected fields are present and also performing additional sanitization
 * on image details, pair configurations, and tags.
 *
 * @param {Object} param0 - An object containing the entryDataResponse.
 * @param {Object} param0.entryDataResponse - The entry data response to be sanitized.
 *
 * @returns {Object} The sanitized response object. This object will have the same shape
 * as the defaultValues object, but its values will be replaced with the corresponding
 * values from the entryDataResponse object if they exist. If a field is not present in
 * the entryDataResponse object, the default value will be used instead. Furthermore,
 * image details, pair configurations, and tags are further sanitized with the
 * sanitizeImageDetails, sanitizePairConfiguration, and sanitizeTags functions, respectively.
 */
export const sanitizeComparisonEntryDataResponse = ({ entryDataResponse }) => {
  const defaultValues = {
    created: "",
    date_modified: "",
    appointment_date: "",
    entry_type: "",
    id: "",
    image_one_0: "",
    image_one_0_details: "{}",
    image_one_1: null,
    image_one_1_details: "{}",
    image_one_2: null,
    image_one_2_details: "{}",
    image_one_3: null,
    image_one_3_details: "{}",
    image_one_4: null,
    image_one_4_details: "{}",
    image_one_5: null,
    image_one_5_details: "{}",
    image_one_6: null,
    image_one_6_details: "{}",
    image_one_7: null,
    image_one_7_details: "{}",
    image_one_8: null,
    image_one_8_details: "{}",
    image_one_9: null,
    image_one_9_details: "{}",
    image_two_0: "",
    image_two_0_details: "{}",
    image_two_1: null,
    image_two_1_details: "{}",
    image_two_2: null,
    image_two_2_details: "{}",
    image_two_3: null,
    image_two_3_details: "{}",
    image_two_4: null,
    image_two_4_details: "{}",
    image_two_5: null,
    image_two_5_details: "{}",
    image_two_6: null,
    image_two_6_details: "{}",
    image_two_7: null,
    image_two_7_details: "{}",
    image_two_8: null,
    image_two_8_details: "{}",
    image_two_9: null,
    image_two_9_details: "{}",
    pair_configuration_0: "{}",
    pair_configuration_1: "{}",
    pair_configuration_2: "{}",
    pair_configuration_3: "{}",
    pair_configuration_4: "{}",
    pair_configuration_5: "{}",
    pair_configuration_6: "{}",
    pair_configuration_7: "{}",
    pair_configuration_8: "{}",
    pair_configuration_9: "{}",
    notes: "",
    patient_owner: "",
    physician_owner: "",
    summary: "",
    classification_tags: [],
    thumbnail: "",
  };

  let sanitizedResponse = sanitizeEntryDataResponseBase({
    entryDataResponse: entryDataResponse,
    defaultValues: defaultValues,
  });

  // Here you would call the sanitizeImageDetails and sanitizePairConfiguration functions on the relevant fields.
  for (let i = 0; i < 10; i++) {
    sanitizedResponse[`image_one_${i}_details`] = sanitizeImageDetails(
      sanitizedResponse[`image_one_${i}_details`]
    );
    sanitizedResponse[`image_two_${i}_details`] = sanitizeImageDetails(
      sanitizedResponse[`image_two_${i}_details`]
    );
    sanitizedResponse[`pair_configuration_${i}`] = sanitizePairConfiguration(
      sanitizedResponse[`pair_configuration_${i}`]
    );
  }
  sanitizedResponse.classification_tags = sanitizeTags(
    sanitizedResponse.classification_tags
  );

  return sanitizedResponse;
};

/**
 * Processes the comparison items of a sanitized entry data response.
 *
 * @param {Object} param0 - An object containing the sanitizedEntryDataResponse.
 * @param {Object} param0.sanitizedEntryDataResponse - The sanitized entry data response that contains the comparison items to be processed.
 *
 * @returns {Object} An object that contains the processed comparison items.
 *
 * @example
 * const processedItems = processComparisonItems({ sanitizedEntryDataResponse: mySanitizedResponse });
 * console.log(processedItems.processedComparisonItems); // Logs the processed comparison items
 */
export const processComparisonItems = ({ sanitizedEntryDataResponse }) => {
  const imagePairs = [];
  for (let i = 0; i < 10; i++) {
    imagePairs.push({
      image_one_name: `image_one_${i}`,
      image_two_name: `image_two_${i}`,
    });
  }

  const { processedComparisonItems } = processSettingImagePairs({
    sanitizedEntryDataResponse,
    imagePairs,
    fillEmptyComparisonItem: false,
  });

  return { processedComparisonItems };
};

/**
 * Processes the comparison save form data by appending images and their details, along with valid pair configuration, to the form data.
 *
 * @param {Object} param0 - An object containing formData and comparisonItems.
 * @param {Object} param0.formData - The form data to which the images, their details, and valid pair configuration will be appended.
 * @param {Array} param0.comparisonItems - The array of comparison items that contain the files and their details.
 *
 * @returns {boolean} Returns true if the images and their details, along with valid pair configuration, are successfully appended to the form data. Otherwise, it displays a toast warning and returns false.
 *
 * @example
 * const isProcessed = processComparisonSaveFormData({ formData: myFormData, comparisonItems: myComparisonItems });
 * console.log(isProcessed); // Logs true if the data is successfully processed, false otherwise
 */
export const processComparisonSaveFormData = ({
  formData,
  comparisonItems,
}) => {
  for (let i = 0; i < comparisonItems.length; i++) {
    const {
      fileOne,
      fileTwo,

      fileOneDetails,
      fileTwoDetails,
    } = comparisonItems[i];

    const fileOneName = `image_one_${i}`;
    const fileTwoName = `image_two_${i}`;
    const detailsOneName = `image_one_${i}_details`;
    const detailsTwoName = `image_two_${i}_details`;

    // When any of the images in the pairs are empty, break out of call
    if (fileOne.length === 0 || fileTwo.length === 0) {
      toastManager(
        "warning",
        "Save Warning",
        "Cannot save entry with missing pair images. Add images to pairs, remove empty pairs, or delete the entry."
      );
      return false;
    }

    appendImagesToFormDataCustomField({
      formData: formData,
      fileInput: fileOne,
      fileDetails: fileOneDetails,
      fileName: fileOneName,
      detailsName: detailsOneName,
    });

    appendImagesToFormDataCustomField({
      formData: formData,
      fileInput: fileTwo,
      fileDetails: fileTwoDetails,
      fileName: fileTwoName,
      detailsName: detailsTwoName,
    });

    // Get the valid pair configuration and append it to the formData
    const validPairConfiguration = getValidPairConfiguration({
      comparisonItem: comparisonItems[i],
    });
    formData.append(
      `pair_configuration_${i}`,
      JSON.stringify(validPairConfiguration)
    );
  }
  return true;
};
